import { useState, useEffect } from 'react'
import { supabase, createClerkSupabaseClient } from '@/lib/supabase'
import { useUser } from '@/lib/hooks'
import { useGetToken } from '@/lib/hooks/use-auth'

export interface Organization {
  id: string
  name: string
  slug: string
  created_by: string
  created_at: string
  updated_at: string
  settings: any
  is_active: boolean
}

export interface OrganizationMember {
  id: string
  organization_id: string
  user_id: string
  role: 'owner' | 'admin' | 'member'
  joined_at: string
}

export interface UserOrganization extends Organization {
  role: 'owner' | 'admin' | 'member'
}

export function useOrganization() {
  const { profile: user } = useUser()
  const getToken = useGetToken()
  const [organizations, setOrganizations] = useState<UserOrganization[]>([])
  const [currentOrganization, setCurrentOrganization] = useState<UserOrganization | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load user's organizations
  useEffect(() => {
    if (user) {
      loadOrganizations()
    }
  }, [user])

  const loadOrganizations = async () => {
    if (!user?.id) return

    try {
      setLoading(true)
      setError(null)

      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)

      const { data, error } = await authenticatedClient
        .rpc('get_user_organizations', { p_user_id: user.id })

      if (error) throw error

      const orgs = data as UserOrganization[]
      setOrganizations(orgs)

      // Set current organization from localStorage or first available
      const savedOrgId = localStorage.getItem('currentOrganizationId')
      if (savedOrgId) {
        const savedOrg = orgs.find(o => o.id === savedOrgId)
        if (savedOrg) {
          setCurrentOrganization(savedOrg)
        } else if (orgs.length > 0) {
          setCurrentOrganization(orgs[0])
          localStorage.setItem('currentOrganizationId', orgs[0].id)
        }
      } else if (orgs.length > 0) {
        setCurrentOrganization(orgs[0])
        localStorage.setItem('currentOrganizationId', orgs[0].id)
      }
    } catch (err: any) {
      console.error('Error loading organizations:', err)
      setError(err.message || 'Failed to load organizations')
    } finally {
      setLoading(false)
    }
  }

  const switchOrganization = (orgId: string) => {
    const org = organizations.find(o => o.id === orgId)
    if (org) {
      setCurrentOrganization(org)
      localStorage.setItem('currentOrganizationId', orgId)
      // Reload the page to refresh all data for the new organization
      window.location.reload()
    }
  }

  const createOrganization = async (name: string) => {
    if (!user) throw new Error('User not authenticated')

    // Create authenticated Supabase client with Clerk token
    const authenticatedClient = createClerkSupabaseClient(getToken)

    const { data, error } = await authenticatedClient
      .from('organizations')
      .insert({
        name,
        created_by: user.id
      })
      .select()
      .single()

    if (error) throw error

    // Add user as owner
    const { error: memberError } = await authenticatedClient
      .from('organization_members')
      .insert({
        organization_id: data.id,
        user_id: user.id,
        role: 'owner'
      })

    if (memberError) throw memberError

    // Reload organizations
    await loadOrganizations()

    return data
  }

  const inviteToOrganization = async (email: string, role: 'admin' | 'member' = 'member') => {
    if (!currentOrganization) throw new Error('No organization selected')
    if (!user) throw new Error('User not authenticated')

    // Create authenticated Supabase client with Clerk token
    const authenticatedClient = createClerkSupabaseClient(getToken)

    const { data, error } = await authenticatedClient
      .rpc('invite_to_organization', {
        p_email: email,
        p_organization_id: currentOrganization.id,
        p_role_id: role === 'admin' ? 1 : 2 // Assuming 1 is admin, 2 is employee
      })

    if (error) throw error
    return data
  }

  return {
    organizations,
    currentOrganization,
    loading,
    error,
    switchOrganization,
    createOrganization,
    inviteToOrganization,
    reloadOrganizations: loadOrganizations
  }
}